<template>
  <div class="modal-backdrop" @click.self="close">
    <section
      class="modal-window modal-confirm"
      role="dialog"
      aria-modal="true"
      aria-labelledby="user-delete-modal-title"
    >
      <header class="modal-header">
        <div>
          <span class="modal-category">Confirmación</span>
          <h3 id="user-delete-modal-title">Eliminar usuario</h3>
        </div>

        <button
          type="button"
          class="modal-close"
          aria-label="Cerrar"
          @click="close"
        >
          ×
        </button>
      </header>

      <div class="modal-body">

        <p v-if="loading" class="modal-note">Revisando qué datos tiene este usuario...</p>

        <!-- Cuenta propia: el backend rechaza el borrado, así que no se ofrece. -->
        <template v-else-if="isSelf">
          <p class="modal-question">No puedes eliminar tu propia cuenta.</p>
          <p class="modal-note">
            Estás con la sesión de {{ displayName }}. Para dar de baja esta cuenta,
            pídele a otra persona con permiso de administración de usuarios que lo haga.
          </p>
        </template>

        <template v-else>
          <p class="modal-question">¿Realmente deseas eliminar a {{ displayName }}?</p>

          <p v-if="impactError" class="modal-note modal-note--warning">
            No fue posible revisar los datos de este usuario. Puedes eliminarlo de todas formas,
            pero no sabrás qué queda en la base de datos.
          </p>

          <p v-else-if="!hasOwnData" class="modal-note">
            Este usuario no tiene datos propios asociados.
          </p>

          <template v-else>
            <div v-if="deletedItems.length" class="impact-block">
              <h4 class="impact-block__title">Se eliminará</h4>
              <ul class="impact-list">
                <li v-for="item in deletedItems" :key="item.label" class="impact-row">
                  <span class="impact-row__count">{{ item.count }}</span>
                  <span class="impact-row__text">
                    <span class="impact-row__label">{{ item.label }}</span>
                    <span v-if="item.hint" class="impact-row__hint">{{ item.hint }}</span>
                  </span>
                </li>
              </ul>
            </div>

            <div v-if="orphanedItems.length" class="impact-block">
              <h4 class="impact-block__title">Quedará en la base sin dueño</h4>
              <ul class="impact-list">
                <li v-for="item in orphanedItems" :key="item.label" class="impact-row">
                  <span class="impact-row__count">{{ item.count }}</span>
                  <span class="impact-row__text">
                    <span class="impact-row__label">{{ item.label }}</span>
                  </span>
                </li>
              </ul>
              <p class="impact-footnote">
                Hoy el sistema no borra estos datos al eliminar un usuario: quedan
                apuntando a una cuenta que ya no existe.
              </p>
            </div>
          </template>

          <div class="impact-block impact-block--untouched">
            <h4 class="impact-block__title">No se ve afectado</h4>
            <ul>
              <li>Capas, mapas y documentos: son recursos institucionales y no pertenecen a ningún usuario.</li>
              <li>El historial de auditoría de las acciones de este usuario.</li>
            </ul>
          </div>
        </template>

      </div>

      <footer class="modal-footer">
        <button
          type="button"
          class="modal-button"
          @click="close"
        >
          {{ isSelf ? 'Volver' : 'Cancelar' }}
        </button>

        <button
          v-if="!isSelf"
          type="button"
          class="modal-button modal-button-delete"
          :disabled="loading || deleting"
          @click="confirm"
        >
          {{ deleting ? 'Eliminando...' : 'Sí, eliminar' }}
        </button>
      </footer>
    </section>
  </div>
</template>

<script>
// Confirmación de borrado de usuario (QA-04): muestra qué datos propios tiene la
// cuenta antes de que el administrador confirme. Vive como modal para no sacar al
// usuario del listado ni de la ficha desde donde disparó la acción.
export default {
  name: 'UserDeleteModal',
  props: {
    userId: { type: String, required: true },
    userName: { type: String, default: '' }
  },
  data() {
    return {
      impact: null,
      isSelf: false,
      loading: true,
      impactError: false,
      deleting: false
    }
  },
  computed: {
    displayName() {
      return this.userName || 'este usuario'
    },
    // Lo único que DeleteOneUser borra hoy en cascada.
    deletedItems() {
      if (!this.impact) return []
      return this.countedItems([
        {
          label: 'Mapas asignados',
          hint: 'Se quita la asignación; el mapa no se borra.',
          count: this.impact.assigned_maps
        }
      ])
    },
    // Datos propios que el borrado no toca y quedan huérfanos (QA-10).
    //
    // Las capas externas (QA-154) van acá y no en deletedItems porque
    // DeleteOneUser tampoco las borra: este bloque describe lo que el backend
    // hace hoy. SENAPRED pidió expresamente que el archivo sobreviva a la
    // cuenta, para poder rescatarlo antes de decidir si se elimina.
    orphanedItems() {
      if (!this.impact) return []
      return this.countedItems([
        { label: 'Anotaciones', count: this.impact.annotations },
        { label: 'Filtros guardados', count: this.impact.saved_filters },
        { label: 'Adjuntos de terreno', count: this.impact.terrain_attachments },
        { label: 'Cuadrillas en las que participa', count: this.impact.terrain_crews },
        { label: 'Capas externas subidas (SHP/KMZ/GeoJSON)', count: this.impact.external_layers }
      ])
    },
    hasOwnData() {
      return this.deletedItems.length > 0 || this.orphanedItems.length > 0
    }
  },
  methods: {
    // Solo se listan los tipos con al menos un registro: mostrar filas en cero
    // llena el modal de ruido y esconde lo que sí importa.
    countedItems(items) {
      return items.filter(item => item.count > 0)
    },
    close() {
      this.$emit('close')
    },
    onKeydown(event) {
      if (event.key === 'Escape') this.close()
    },
    loadImpact() {
      const backendurl = this.$store.getters.backendurl
      this.$http.get(`${backendurl}api/v1/users/${this.userId}/delete-impact`)
        .then((response) => {
          this.impact = response.data
          this.isSelf = !!response.data.is_self
          this.loading = false
        })
        .catch((error) => {
          console.log(error)
          // El inventario es informativo: si falla, el borrado sigue disponible.
          this.impactError = true
          this.loading = false
        })
    },
    confirm() {
      const backendurl = this.$store.getters.backendurl
      this.deleting = true
      this.$http.delete(`${backendurl}api/v1/users/${this.userId}`)
        .then(() => {
          this.deleting = false
          // El toast vive en App.vue, así que sobrevive al cierre del modal y al
          // cambio de ruta que hace la ficha después de borrar.
          this.$store.dispatch('toast/show', {
            message: `Usuario ${this.displayName} eliminado`,
            type: 'success'
          })
          this.$emit('deleted')
        })
        .catch((error) => {
          console.log(error)
          this.deleting = false
          // El modal queda abierto para poder reintentar. Fallas de red no traen error.response.
          this.$store.dispatch('toast/show', {
            message: (error.response && error.response.data && error.response.data.message) ||
              'No fue posible eliminar el usuario.',
            type: 'error'
          })
        })
    }
  },
  mounted() {
    this.loadImpact()
    document.addEventListener('keydown', this.onKeydown)
  },
  beforeDestroy() {
    document.removeEventListener('keydown', this.onKeydown)
  }
}
</script>

<style lang="scss" scoped>
// Mismo lenguaje visual que el modal de UsersNotifications.vue, más el
// difuminado del fondo que pidió Big Boss para separar la confirmación
// destructiva del resto de la página.
.modal-backdrop {
  position: fixed;
  inset: 0;
  z-index: 1000;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 2em;
  background: color-mix(in srgb, var(--color-dark) 55%, transparent);
  backdrop-filter: blur(4px);
  -webkit-backdrop-filter: blur(4px);
}

.modal-window {
  width: 100%;
  max-width: 760px;
  max-height: 88vh;
  display: flex;
  flex-direction: column;
  overflow: hidden;
  border: 1px solid var(--color-fog);
  border-radius: 6px;
  background: var(--color-white);
}

.modal-confirm {
  max-width: 560px;
}

.modal-header {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  gap: 1em;
  padding: 1em 1.3em;
  border-bottom: 1px solid var(--color-fog);
  background: var(--color-navy);
  color: var(--color-white);
}

.modal-header h3 {
  margin: 0.15em 0 0;
  color: var(--color-white);
  font-size: 1.1em;
}

.modal-category {
  color: var(--color-orange);
  font-size: 0.72em;
  font-weight: 700;
  letter-spacing: 0.08em;
  text-transform: uppercase;
}

.modal-close {
  width: 29px;
  height: 29px;
  border: 1px solid var(--color-steel);
  border-radius: 4px;
  background: transparent;
  color: var(--color-white);
  font-size: 1.25em;
  cursor: pointer;
}

.modal-close:hover {
  border-color: var(--color-orange);
  color: var(--color-orange);
}

.modal-body {
  padding: 1.3em;
  overflow-y: auto;
  color: var(--color-graphite);
}

.modal-footer {
  display: flex;
  justify-content: flex-end;
  gap: 0.7em;
  padding: 0.9em 1.3em;
  border-top: 1px solid var(--color-fog);
  background: var(--color-mist);
}

.modal-button {
  padding: 0.5em 1em;
  border: 1px solid var(--color-steel);
  border-radius: 4px;
  background: var(--color-white);
  color: var(--color-steel);
  font-family: inherit;
  font-size: 0.82em;
  font-weight: 600;
  cursor: pointer;
}

.modal-button:hover:not(:disabled) {
  background: var(--color-steel);
  color: var(--color-white);
}

.modal-button:disabled {
  opacity: 0.6;
  cursor: default;
}

.modal-button-delete {
  border-color: var(--color-danger);
  color: var(--color-danger);
}

.modal-button-delete:hover:not(:disabled) {
  background: var(--color-danger);
  border-color: var(--color-danger);
  color: var(--color-white);
}

.modal-question {
  margin: 0 0 1rem;
  font-size: 0.95rem;
  font-weight: 600;
  color: var(--color-graphite);
}

.modal-note {
  margin: 0 0 1rem;
  font-size: 0.85rem;
  color: var(--color-ghost);
}

.modal-note--warning {
  color: var(--color-warning);
}

.impact-block {
  margin-bottom: 1.1rem;

  &__title {
    margin: 0 0 0.5rem;
    padding-bottom: 0.4rem;
    border-bottom: 1px solid var(--color-line);
    font-size: 0.74rem;
    font-weight: 700;
    text-transform: uppercase;
    letter-spacing: 0.05em;
    color: var(--color-steel);
  }

  &--untouched {
    margin-bottom: 0;

    ul {
      margin: 0;
      padding-left: 1.1rem;
    }

    li {
      font-size: 0.85rem;
      color: var(--color-graphite);
      padding: 0.15rem 0;
    }
  }
}

.impact-list {
  margin: 0;
  padding: 0;
  list-style: none;
}

// El número va primero y con peso: es el dato que el administrador está
// buscando, y así no queda perdido a media ficha en una grilla etiqueta/valor.
.impact-row {
  display: flex;
  align-items: baseline;
  gap: 0.75rem;
  padding: 0.35rem 0;

  & + & {
    border-top: 1px solid var(--color-line);
  }

  &__count {
    flex: 0 0 2.2rem;
    font-size: 1.15rem;
    font-weight: 700;
    color: var(--color-navy);
    text-align: right;
    font-variant-numeric: tabular-nums;
  }

  &__text {
    display: flex;
    flex-direction: column;
    gap: 0.15rem;
  }

  &__label {
    font-size: 0.88rem;
    color: var(--color-graphite);
  }

  &__hint {
    font-size: 0.76rem;
    color: var(--color-ghost);
  }
}

.impact-footnote {
  margin: 0.6rem 0 0;
  font-size: 0.78rem;
  color: var(--color-ghost);
}
</style>
